import React from 'react';
import { Outlet, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import PortalLayout from './PortalLayout';
import PortalSpaceChrome, { PortalBrandLockup } from './PortalSpaceChrome';
import EmployeeDashboard from '../../pages/EmployeePortal/EmployeeDashboard';
import TeacherDashboard from './Work/TeacherDashboard';
import ForegroundPushListener from './ForegroundPushListener';
import { GuideProvider } from './Guide/GuideContext';

/* Portal Phase E3a — role-rendered Work shell.
   /portal/work is the canonical staff home. The CHROME differs by role:
   - role === 'admin'  → the existing admin dashboard shell (PortalLayout: the
     grouped sidebar + admin tools). Its <Outlet/> renders the migrated admin
     pages (today, students, scheduling, tasks, …).
   - non-admin employee (teacher) → the Phase B single-space PortalSpaceChrome
     framing (no admin sidebar). In E3a this is a SAFE PLACEHOLDER — it must not
     mount the admin task module or issue any deny-by-default query. The real
     teacher dashboard (her tasks / students / schedule) lands in E3b.

   Both branches render <Outlet/>, so the same nested child routes resolve into
   whichever chrome the role selects. A teacher has no nav links into the admin
   sub-pages, and the Phase E1 Firestore rules deny their data regardless, so the
   placeholder is safe until E3b adds permission-aware gating. */
function WorkShell() {
	const { userProfile } = useAuth();
	const isAdmin = userProfile?.role === 'admin';

	if (isAdmin) {
		// PortalLayout renders its own <Outlet/>; the child routes resolve there.
		// ForegroundPushListener shows an in-app notice when a task push arrives
		// while the tab is open + focused (SW background display is skipped then).
		// GuideProvider sits above the routes so a guided tour survives the
		// page changes it asks for (Today -> Start -> the Lesson Dashboard).
		return (
			<GuideProvider>
				<ForegroundPushListener />
				<PortalLayout />
			</GuideProvider>
		);
	}

	return (
		<GuideProvider>
			<PortalSpaceChrome leading={<PortalBrandLockup />}>
				<ForegroundPushListener />
				<Outlet />
			</PortalSpaceChrome>
		</GuideProvider>
	);
}

// Index element for /portal/work. The shell above already picked the chrome;
// this picks the home body to match: the admin dashboard, or (E3b) the real
// role-rendered teacher dashboard with its gated tasks/students/schedule.
export function WorkHome() {
	const { userProfile } = useAuth();
	const [searchParams] = useSearchParams();
	const isAdmin = userProfile?.role === 'admin';

	// Deep-link: task pushes / the Scriptable widget link to
	// /portal/work?task=<id>. The teacher branch (TeacherDashboard → scoped
	// WorkTasksModule) already reads ?task, but EmployeeDashboard (admin home)
	// does not. Forward an admin with a ?task to the canonical Tasks page, which
	// mounts WorkTasksModule unscoped (admins may read all tasks) and opens the
	// task's detail from the same param. Teachers must NOT be sent there — the
	// unscoped route is denied by rules for non-admins; their scoped path below
	// handles the deep-link already.
	const taskId = searchParams.get('task');
	if (isAdmin && taskId) {
		return (
			<Navigate
				to={`/portal/work/tasks?task=${encodeURIComponent(taskId)}`}
				replace
			/>
		);
	}

	return isAdmin ? <EmployeeDashboard /> : <TeacherDashboard />;
}

export default WorkShell;
